/**
 * Native screening orchestration.
 *
 * Drives Forma's native analyses (sun, noise) live and returns their normalized
 * results so callers can turn them into `simulation-evidence/v1`. It composes
 * the reuse-first sun wrapper and the noise trigger, runs the requested studies
 * concurrently, and degrades each study to a captured failure instead of
 * throwing — one failing study never kills the others. The output stays raw
 * analysis results; turning them into evidence remains the caller's job so the
 * existing analyzers (`analyzeSunShadowScreening`, `analyzeNoiseScreening`) stay
 * the single normalization path.
 */

import {
  getOrTriggerSunAnalysis,
  triggerNoiseAnalysis,
  type FormaNativeAnalysisClient,
  type NoiseAnalysisResult,
  type SunAnalysisResult,
} from './index';

/** Sun screening request: the date drives the analysis, so it is required. */
export interface NativeSunScreeningRequest {
  /** Month (1-12) the native sun analysis is computed for. */
  month: number;
  /** Day of month (1-31) the native sun analysis is computed for. */
  date: number;
  /** Optional root URN scoping reuse lookups and the trigger. */
  rootUrn?: string;
  /** Optional element URN prefix used to narrow the reuse search. */
  elementUrnPrefix?: string;
}

/** Noise screening request. Empty object simply requests a noise run. */
export interface NativeNoiseScreeningRequest {
  /** Optional root URN scoping the trigger. */
  rootUrn?: string;
}

export interface CollectNativeScreeningOptions {
  forma: FormaNativeAnalysisClient;
  /** Forma element paths to analyze; an empty selection fails each study. */
  selectedElementPaths: readonly string[];
  /** When set, runs sun screening (reuse-first) for this date. Omit to skip. */
  sun?: NativeSunScreeningRequest;
  /** When set, triggers noise screening. Omit to skip. */
  noise?: NativeNoiseScreeningRequest;
}

/** A requested study that could not produce a result, with a reason. */
export interface NativeScreeningFailure {
  studyId: 'sun' | 'noise';
  message: string;
}

/** Live native screening outcome: successful results plus captured failures. */
export interface NativeScreeningResult {
  sun?: SunAnalysisResult;
  noise?: NoiseAnalysisResult;
  failures: readonly NativeScreeningFailure[];
}

/**
 * Runs the requested native screenings live against Forma and returns their
 * results. Sun reuses a matching SUCCEEDED analysis before triggering; noise is
 * triggered. Studies run concurrently and each failure is captured rather than
 * thrown, so a partial Forma model still yields whatever screenings succeeded.
 */
export async function collectNativeScreening(
  options: CollectNativeScreeningOptions,
): Promise<NativeScreeningResult> {
  const selectedElementPaths = [...options.selectedElementPaths];
  const [sunOutcome, noiseOutcome] = await Promise.all([
    runSunScreening(options.forma, selectedElementPaths, options.sun),
    runNoiseScreening(options.forma, selectedElementPaths, options.noise),
  ]);
  const failures: NativeScreeningFailure[] = [];

  if (sunOutcome?.ok === false) {
    failures.push({ studyId: 'sun', message: sunOutcome.message });
  }

  if (noiseOutcome?.ok === false) {
    failures.push({ studyId: 'noise', message: noiseOutcome.message });
  }

  return {
    ...(sunOutcome?.ok === true ? { sun: sunOutcome.result } : {}),
    ...(noiseOutcome?.ok === true ? { noise: noiseOutcome.result } : {}),
    failures,
  };
}

type StudyOutcome<Result> = { ok: true; result: Result } | { ok: false; message: string };

async function runSunScreening(
  forma: FormaNativeAnalysisClient,
  selectedElementPaths: string[],
  request: NativeSunScreeningRequest | undefined,
): Promise<StudyOutcome<SunAnalysisResult> | undefined> {
  if (request === undefined) {
    return undefined;
  }

  if (selectedElementPaths.length === 0) {
    return { ok: false, message: 'No selected elements to run sun screening.' };
  }

  try {
    const result = await getOrTriggerSunAnalysis({
      forma,
      selectedElementPaths,
      month: request.month,
      date: request.date,
      ...(request.rootUrn === undefined ? {} : { rootUrn: request.rootUrn }),
      ...(request.elementUrnPrefix === undefined
        ? {}
        : { elementUrnPrefix: request.elementUrnPrefix }),
    });

    return { ok: true, result };
  } catch (error) {
    return { ok: false, message: describeError(error) };
  }
}

async function runNoiseScreening(
  forma: FormaNativeAnalysisClient,
  selectedElementPaths: string[],
  request: NativeNoiseScreeningRequest | undefined,
): Promise<StudyOutcome<NoiseAnalysisResult> | undefined> {
  if (request === undefined) {
    return undefined;
  }

  if (selectedElementPaths.length === 0) {
    return { ok: false, message: 'No selected elements to run noise screening.' };
  }

  try {
    const result = await triggerNoiseAnalysis({
      forma,
      selectedElementPaths,
      ...(request.rootUrn === undefined ? {} : { rootUrn: request.rootUrn }),
    });

    return { ok: true, result };
  } catch (error) {
    return { ok: false, message: describeError(error) };
  }
}

function describeError(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}
